import {
  Cloud,
  DailyAppVm,
  DailyBreakdownRowVm,
  DailyPointVm,
  MonthlyPointVm,
  UsageDashboardVm,
} from 'src/app/account/components/usage-billing/usage-billing.model'

export interface UsageBillingScenario {
  key: string
  label: string
  description: string
  // optgroup bucket in the picker.
  group: 'CONTRACT' | 'CYCLE' | 'DATA' | 'SCALE' | 'LIFECYCLE'
  // null for the lifecycle scenarios, which drive loading / error instead of
  // resolving to a view model.
  vm: UsageDashboardVm | null
  lifecycle?: 'loading' | 'error'
  // Entitlement behind the header's contract facts. Held apart from `vm` because those
  // read AccountService, not the usage endpoints — which is why they still render for
  // the loading and error scenarios.
  contract: UsageBillingContract
}

/** The entitlement facts behind the header and the contracted figure, per scenario. */
export interface UsageBillingContract {
  productName: string
  /** Licensed count, used as the contracted baseline when the usage report omits one. */
  licensedBillableResourceCount: number
  /** ISO date. A past date puts the expiration into its expired treatment. */
  expiration: string
  /** Drives `overageEnforcementEnabled`; noOverage stays false or the page isn't Flex. */
  enforceLimits: boolean
}

const DEFAULT_CONTRACT: UsageBillingContract = {
  productName: 'Arpio Enterprise',
  licensedBillableResourceCount: 500,
  expiration: '2027-03-31',
  enforceLimits: false,
}

/** Contract facts for a scenario, defaulting anything it doesn't care about. */
function usageContract(overrides: Partial<UsageBillingContract> = {}): UsageBillingContract {
  return { ...DEFAULT_CONTRACT, ...overrides }
}

const USAGE_MONTH_ABBRS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function usageDayLabel(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${USAGE_MONTH_ABBRS[m - 1]} ${d}`
}

/** Adds `days` to an ISO date, staying in UTC so month rollovers are exact. */
function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

interface DailySeriesOpts {
  start: string
  days: number
  contracted: number | null
  /** Billable count per day. Receives the zero-based day index. */
  billable: (i: number) => number
  /** Total resources per day; defaults to ~1.8x billable (shared resources dedupe). */
  total?: (i: number, billable: number) => number
  /** Share of the billable count attributed to Azure. 0 = AWS-only. */
  azureShare?: number
}

function buildUsageDaily(opts: DailySeriesOpts): DailyPointVm[] {
  const { start, days, contracted, billable, azureShare = 0.3 } = opts
  const total = opts.total ?? ((_i: number, b: number) => Math.round(b * 1.8))
  return Array.from({ length: days }, (_, i) => {
    const date = addDays(start, i)
    const b = billable(i)
    const t = total(i, b)
    const azureBillable = Math.round(b * azureShare)
    const azureTotal = Math.round(t * azureShare)
    return {
      date,
      dayLabel: usageDayLabel(date),
      billable: b,
      total: t,
      awsBillable: b - azureBillable,
      azureBillable,
      awsTotal: t - azureTotal,
      azureTotal,
      contracted,
      over: contracted != null && b > contracted,
    }
  })
}

interface MonthlySeriesOpts {
  /** Oldest month key, YYYY-MM. */
  start: string
  months: number
  contracted: number | null
  hwm: (i: number) => number
  azureShare?: number
}

function buildUsageMonthly(opts: MonthlySeriesOpts): MonthlyPointVm[] {
  const { start, months, contracted, hwm, azureShare = 0.3 } = opts
  const [startYear, startMonth] = start.split('-').map(Number)
  return Array.from({ length: months }, (_, i) => {
    const monthIndex = startMonth - 1 + i
    const year = startYear + Math.floor(monthIndex / 12)
    const month = (monthIndex % 12) + 1
    const value = hwm(i)
    const azureHwm = Math.round(value * azureShare)
    return {
      monthKey: `${year}-${String(month).padStart(2, '0')}`,
      label: `${USAGE_MONTH_ABBRS[month - 1]} ${year}`,
      hwm: value,
      awsHwm: value - azureHwm,
      azureHwm,
      contracted,
    }
  })
}

const USAGE_APP_NAMES = [
  'MOCK-TEST Payments API',
  'Customer Portal',
  'Order Fulfillment',
  'Identity Service',
  'Reporting Warehouse',
  'Notification Hub',
  'Inventory Sync',
  'Partner Gateway',
  'Search Indexer',
  'Audit Archive',
  'Billing Ledger',
  'Media Transcoder',
  'Fraud Scoring',
  'Data Lake ETL',
]

const AWS_TYPES = ['ec2Instance', 'rdsInstance', 'ebsVolume', 'lambdaFunction', 'dynamoDbTable']

const AZURE_TYPES = [
  'Microsoft.Compute/virtualMachines',
  'Microsoft.Sql/servers/databases',
  'Microsoft.Compute/disks',
  'Microsoft.Web/sites',
]

/** Top-N apps for a breakdown day, alternating clouds and splitting each app's
 *  count across a handful of resource types. */
/**
 * Which of `count` applications are Azure, spread through the list rather than blocked at
 * one end so the table's cloud column looks mixed. Marks exactly `azureCount` of them.
 */
function usageAppClouds(count: number, azureCount: number): Cloud[] {
  return Array.from({ length: count }, (_, i) =>
    Math.floor((i * azureCount) / count) !== Math.floor(((i + 1) * azureCount) / count) ? 'AZURE' : 'AWS'
  )
}

function buildUsageApps(count: number, peakBillable: number, azureCount: number, typesPerApp = 3): DailyAppVm[] {
  // Geometric-ish falloff so the bar column shows a realistic spread.
  const weights = Array.from({ length: count }, (_, i) => Math.pow(0.72, i))
  const weightSum = weights.reduce((s, w) => s + w, 0)
  const clouds = usageAppClouds(count, azureCount)
  return Array.from({ length: count }, (_, i) => {
    const cloud: Cloud = clouds[i]
    const types = cloud === 'AZURE' ? AZURE_TYPES : AWS_TYPES
    const billableCount = Math.max(1, Math.round((weights[i] / weightSum) * peakBillable))
    const n = Math.min(typesPerApp, types.length)
    // Distribute the app's count across its types, remainder on the first.
    const per = Math.floor(billableCount / n)
    const byType = Array.from({ length: n }, (_, t) => ({
      resourceType: types[t],
      count: t === 0 ? billableCount - per * (n - 1) : per,
    })).filter((t) => t.count > 0)
    return {
      appId: `app-${i + 1}`,
      appName: USAGE_APP_NAMES[i % USAGE_APP_NAMES.length],
      cloud,
      billableCount,
      byType,
    }
  })
}

function buildUsageBreakdown(
  daily: DailyPointVm[],
  contracted: number | null,
  appCount: number,
  azureCount: number
): DailyBreakdownRowVm[] {
  return daily.map((d) => ({
    date: d.date,
    dayLabel: d.dayLabel,
    billable: d.billable,
    delta: contracted == null ? 0 : d.billable - contracted,
    topApps: d.billable > 0 ? buildUsageApps(appCount, d.billable, azureCount) : [],
  }))
}

interface UsageVmOpts {
  contracted: number | null
  monthlyHwm: number
  /** Peak day label for the HWM tile; null suppresses it (zero-usage cycles). */
  peakDayLabel?: string | null
  cycleStart: string
  cycleEnd: string
  /** false = ARP-7926 fields absent, bounds derived from the daily records. */
  exactBounds?: boolean
  isCurrent?: boolean
  daily: DailyPointVm[]
  monthly: MonthlyPointVm[]
  /** Oldest cycle the trend endpoint covered, before the data-start floor. Defaults to
   *  where `monthly` starts; set it earlier to make the pre-records range selectable. */
  trendWindowStart?: string
  /**
   * Applications with billable usage in the snapshot the breakdown table renders. Set
   * appsAws/appsAzure higher than this to model an account whose remaining applications
   * billed nothing, which is what the table's exclusion note accounts for.
   */
  appCount?: number
  /** Overrides the derived AWS/Azure split of the account's own application list. */
  appsAws?: number
  appsAzure?: number
}

function buildUsageVm(opts: UsageVmOpts): UsageDashboardVm {
  const {
    contracted,
    monthlyHwm,
    cycleStart,
    cycleEnd,
    exactBounds = true,
    isCurrent = true,
    daily,
    monthly,
    appCount = 8,
  } = opts
  const last = daily[daily.length - 1]
  const peak = daily.reduce<DailyPointVm | null>((max, d) => (max == null || d.billable > max.billable ? d : max), null)
  // Account split first: the snapshot's own mix is derived from it, so the per-cloud
  // arithmetic in the table's exclusion note reconciles with the Applications tile.
  const azureApps = opts.appsAzure ?? Math.floor(appCount / 3)
  const awsApps = opts.appsAws ?? appCount - azureApps
  const totalApps = awsApps + azureApps
  const snapshotAzure = totalApps ? Math.round((appCount * azureApps) / totalApps) : 0
  const breakdown = buildUsageBreakdown(daily, contracted, appCount, snapshotAzure)
  // Mirrors cycleLabel() in usage-billing.service.ts, including the both-years
  // form an Azure anniversary cycle needs when it straddles a year boundary.
  const [startYear, endYear] = [cycleStart.slice(0, 4), cycleEnd.slice(0, 4)]
  const from = startYear === endYear ? usageDayLabel(cycleStart) : `${usageDayLabel(cycleStart)}, ${startYear}`
  return {
    billingCycle: {
      monthKey: cycleStart.slice(0, 7),
      label: `${from} – ${usageDayLabel(cycleEnd)}, ${endYear}`,
      start: cycleStart,
      end: exactBounds ? cycleEnd : (last?.date ?? cycleEnd),
      exactBounds,
      isCurrent,
    },
    kpis: {
      monthlyHwm,
      monthlyHwmDateLabel:
        opts.peakDayLabel !== undefined ? opts.peakDayLabel : monthlyHwm > 0 && peak ? peak.dayLabel : null,
      contracted,
      within: contracted == null || monthlyHwm <= contracted,
      delta: contracted == null ? 0 : monthlyHwm - contracted,
      currentBillable: last?.billable ?? null,
      currentAsOfLabel: last ? last.dayLabel : '—',
      appsAws: awsApps,
      appsAzure: azureApps,
      appsTotal: totalApps,
    },
    // Defaults to where the series starts, which floors the period picker there. A scenario
    // exercising the pre-records message has to set it earlier, the way a real account's
    // trend endpoint reaches back past the floor.
    trendWindowStart: opts.trendWindowStart ?? monthly[0]?.monthKey ?? '',
    monthly,
    daily,
    dailyBreakdown: breakdown,
  }
}

/** Aug 1–19 billable counts for the enterprise-cap scenario: peaks at 1,025 on
 *  Aug 15 and settles at 981, so the KPI tiles read like a real large account. */
const ENTERPRISE_CAP_DAILY = [
  920, 934, 941, 958, 947, 963, 970, 988, 995, 1002, 1011, 1008, 1016, 1020, 1025, 1013, 1004, 992, 981,
]

/** A steady 12-cycle monthly history ending at the given HWM. */
function standardMonthly(contracted: number | null, endHwm: number): MonthlyPointVm[] {
  return buildUsageMonthly({
    start: '2025-09',
    months: 12,
    contracted,
    hwm: (i) => Math.round(endHwm * (0.62 + i * 0.035)),
  })
}

export function buildUsageBillingScenarios(): UsageBillingScenario[] {
  // Anchor every scenario to a fixed cycle so screenshots stay comparable.
  const START = '2026-08-01'
  const END = '2026-08-31'
  const DAYS = 20

  return [
    // ─── Contract state ──────────────────────────────────────────────
    {
      key: 'u01-healthy',
      contract: usageContract({ licensedBillableResourceCount: 500 }),
      label: 'Within contract',
      description:
        'The styling baseline: usage tracks comfortably under the cap. Blue dots throughout, "Within" pill, headroom in the sub-line, dashed contracted line above the series.',
      group: 'CONTRACT',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 412,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 500,
          billable: (i) => 360 + Math.round(Math.sin(i / 2.4) * 28) + i,
        }),
        monthly: standardMonthly(500, 412),
      }),
    },
    {
      key: 'u02-over-contract',
      contract: usageContract({ licensedBillableResourceCount: 400, enforceLimits: true }),
      label: 'Over contract',
      description:
        'High water mark above the cap. Amber "Over" pill, "N over contract" sub-line, amber dots for every day past the line, and the contracted line crossing mid-series.',
      group: 'CONTRACT',
      vm: buildUsageVm({
        contracted: 400,
        monthlyHwm: 468,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 400,
          billable: (i) => 330 + i * 7,
        }),
        monthly: standardMonthly(400, 468),
      }),
    },
    {
      key: 'u03-at-contract',
      contract: usageContract({ licensedBillableResourceCount: 450 }),
      label: 'Exactly at contract',
      description:
        'Boundary case: high water mark equals the cap. Still reads "Within" (the comparison is <=), and the headroom sub-line renders "0 available".',
      group: 'CONTRACT',
      vm: buildUsageVm({
        contracted: 450,
        monthlyHwm: 450,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 450,
          billable: (i) => (i === DAYS - 3 ? 450 : 400 + Math.round(Math.sin(i / 2) * 20)),
        }),
        monthly: standardMonthly(450, 450),
      }),
    },
    {
      key: 'u03b-cap-just-in-scale',
      contract: usageContract({ licensedBillableResourceCount: 1000 }),
      label: 'Cap just inside the scale',
      description:
        'Peak usage is 57% of the cap, above the point where the contracted line stays in the y domain. The dashed line sits near the top of the plot and the series occupies the lower half. Pair with the next scenario: they straddle the threshold from either side.',
      group: 'CONTRACT',
      vm: buildUsageVm({
        contracted: 1000,
        monthlyHwm: 572,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 1000,
          billable: (i) => 500 + Math.round(Math.sin(i / 2.6) * 40) + i * 2,
        }),
        monthly: standardMonthly(1000, 572),
      }),
    },
    {
      key: 'u03c-cap-just-out-of-scale',
      contract: usageContract({ licensedBillableResourceCount: 1000 }),
      label: 'Cap just outside the scale',
      description:
        'The same cap with peak usage at 47%, a hair below the threshold. The contracted line drops out of the chart, the y axis retightens around the series, and the cap is named in the top-right instead. The legend loses its dashed swatch.',
      group: 'CONTRACT',
      vm: buildUsageVm({
        contracted: 1000,
        monthlyHwm: 475,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 1000,
          billable: (i) => 430 + Math.round(Math.sin(i / 2.6) * 30) + i,
        }),
        monthly: standardMonthly(1000, 475),
      }),
    },
    {
      key: 'u03d-quarter-utilization',
      contract: usageContract({ licensedBillableResourceCount: 4000 }),
      label: 'Quarter of contract used',
      description:
        'A comfortably provisioned account at 23% of its cap — the common case for the off-scale annotation at ordinary four-digit magnitudes, rather than the enterprise extreme.',
      group: 'CONTRACT',
      vm: buildUsageVm({
        contracted: 4000,
        monthlyHwm: 930,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 4000,
          billable: (i) => 820 + Math.round(Math.sin(i / 3) * 90) + i * 4,
        }),
        monthly: standardMonthly(4000, 930),
      }),
    },
    {
      key: 'u03e-volatile-under-large-cap',
      contract: usageContract({ licensedBillableResourceCount: 250000 }),
      label: 'Volatile usage, large cap',
      description:
        'Usage swings between roughly 2,200 and 3,600 under a 250,000 cap. The point of retightening the domain: against the cap this is a flat line, and against the data the day-to-day shape is legible.',
      group: 'CONTRACT',
      vm: buildUsageVm({
        contracted: 250000,
        monthlyHwm: 3600,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 250000,
          billable: (i) => 2200 + Math.round(Math.abs(Math.sin(i / 1.7)) * 1400) + (i % 5 === 0 ? 600 : 0),
        }),
        monthly: standardMonthly(250000, 3600),
      }),
    },
    {
      key: 'u04-trial-no-contract',
      contract: usageContract({
        productName: 'Arpio Trial',
        licensedBillableResourceCount: -1,
        expiration: '2026-09-05',
      }),
      label: 'Trial — no contracted cap',
      description:
        'A trial entitlement reports billable_resource_count = -1, which the service normalizes to null. Contracted tile shows "—", the HWM tile drops its ratio and Over pill, and the chart draws no contracted line and no amber dots.',
      group: 'CONTRACT',
      vm: buildUsageVm({
        contracted: null,
        monthlyHwm: 218,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: null,
          billable: (i) => 180 + Math.round(Math.cos(i / 3) * 30) + i,
        }),
        monthly: standardMonthly(null, 218),
      }),
    },
    {
      key: 'u05-zero-contract',
      contract: usageContract({
        productName: 'Arpio Standard',
        licensedBillableResourceCount: 0,
      }),
      label: 'Zero contracted resources',
      description:
        'A real cap of 0 (not the trial sentinel), so every day is over contract. Confirms 0 survives the negative-baseline normalization and that the chart skips the baseline line at 0 while still marking dots amber.',
      group: 'CONTRACT',
      vm: buildUsageVm({
        contracted: 0,
        monthlyHwm: 96,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 0,
          billable: (i) => 70 + i,
        }),
        monthly: standardMonthly(0, 96),
      }),
    },

    // ─── Billing cycle ───────────────────────────────────────────────
    {
      key: 'u06-anniversary-cycle',
      contract: usageContract({ productName: 'Arpio Azure Enterprise', expiration: '2027-02-20' }),
      label: 'Azure anniversary cycle',
      description:
        'Cycle starts on the 20th rather than the 1st, so the header names the basis "billed on the billing-cycle high water mark" instead of the calendar-month wording.',
      group: 'CYCLE',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 430,
        cycleStart: '2026-08-20',
        cycleEnd: '2026-09-19',
        daily: buildUsageDaily({
          start: '2026-08-20',
          days: 14,
          contracted: 500,
          billable: (i) => 390 + i * 3,
        }),
        monthly: standardMonthly(500, 430),
      }),
    },
    {
      key: 'u07-year-straddle',
      contract: usageContract({ expiration: '2027-01-14' }),
      label: 'Cycle straddling a year boundary',
      description:
        'Dec 15 – Jan 14. The cycle label has to carry both years; dropping the start year would read as a 2027 cycle.',
      group: 'CYCLE',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 445,
        cycleStart: '2026-12-15',
        cycleEnd: '2027-01-14',
        daily: buildUsageDaily({
          start: '2026-12-15',
          days: 18,
          contracted: 500,
          billable: (i) => 400 + Math.round(Math.sin(i / 2) * 25) + i,
        }),
        monthly: buildUsageMonthly({ start: '2026-02', months: 12, contracted: 500, hwm: (i) => 300 + i * 12 }),
      }),
    },
    {
      key: 'u08-derived-bounds',
      contract: usageContract(),
      label: 'Cycle bounds unavailable',
      description:
        'ARP-7926 fields absent, so bounds fall back to the daily-record range. The basis clause stays unnamed ("billed on the high water mark for the cycle") because a derived start cannot distinguish calendar-month from anniversary billing, and the cycle end understates the in-progress cycle.',
      group: 'CYCLE',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 405,
        cycleStart: START,
        cycleEnd: END,
        exactBounds: false,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 500,
          billable: (i) => 350 + i * 2,
        }),
        monthly: standardMonthly(500, 405),
      }),
    },
    {
      key: 'u09-closed-cycle',
      contract: usageContract({ expiration: '2026-07-31' }),
      label: 'Closed cycle',
      description:
        'A finished cycle: the "(in progress)" clause drops out of the header because the high water mark can no longer rise.',
      group: 'CYCLE',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 476,
        cycleStart: '2026-07-01',
        cycleEnd: '2026-07-31',
        isCurrent: false,
        daily: buildUsageDaily({
          start: '2026-07-01',
          days: 31,
          contracted: 500,
          billable: (i) => 380 + Math.round(Math.sin(i / 3) * 60) + i * 2,
        }),
        monthly: buildUsageMonthly({ start: '2025-08', months: 12, contracted: 500, hwm: (i) => 300 + i * 14 }),
      }),
    },

    // ─── Data availability ───────────────────────────────────────────
    {
      key: 'u10-monthly-unavailable',
      contract: usageContract(),
      label: 'Monthly trend unavailable',
      description:
        'ARP-7925 endpoint missing, so the monthly series is empty. The Monthly chip disables and its tooltip blames the missing data ("not available for this account yet") rather than the selected range.',
      group: 'DATA',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 398,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 500,
          billable: (i) => 340 + i * 3,
        }),
        monthly: [],
      }),
    },
    {
      key: 'u10b-data-start',
      contract: usageContract(),
      label: 'Trend floored at the first recorded month',
      description:
        'What every real account looks like today: Arpio only started recording snapshots in Mar 2026, so the trend floors there. Set a custom range ending before Mar 1 to see the no-records message take over the chart and the Applications table.',
      group: 'DATA',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 437,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 500,
          billable: (i) => 380 + i * 3,
        }),
        monthly: buildUsageMonthly({ start: '2026-03', months: 6, contracted: 500, hwm: (i) => 307 + i * 26 }),
        // The endpoint covered twelve cycles even though only six survive the floor, so the
        // picker reaches back to Sep 2025 and a range ending before Mar 1 is selectable.
        trendWindowStart: '2025-09',
      }),
    },
    {
      key: 'u11-no-usage',
      contract: usageContract(),
      label: 'No billable usage',
      description:
        'Applications exist but none of them backed up in the cycle, so every snapshot is zero. Exercises the zero-usage caption, the suppressed peak-day label (no false "hit Aug 1"), the flat chart, and the empty Applications tab. This is what a dev or trial account looks like.',
      group: 'DATA',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 0,
        cycleStart: START,
        cycleEnd: END,
        appCount: 6,
        daily: buildUsageDaily({
          start: START,
          days: 6,
          contracted: 500,
          billable: () => 0,
          total: () => 0,
        }),
        monthly: buildUsageMonthly({ start: '2026-03', months: 6, contracted: 500, hwm: () => 0 }),
      }),
    },
    {
      key: 'u12-single-day',
      contract: usageContract(),
      label: 'Single day of data',
      description:
        'A cycle one day old, so the bill period resolves to one day and there is no trend to plot: the Usage trend tab is disabled (hover it for the reason) and the dashboard opens on Applications. Widen the period with the date inputs to re-enable the trend, then collapse it again to a single day.',
      group: 'DATA',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 288,
        cycleStart: START,
        cycleEnd: END,
        daily: buildUsageDaily({ start: START, days: 1, contracted: 500, billable: () => 288 }),
        monthly: buildUsageMonthly({ start: '2026-08', months: 1, contracted: 500, hwm: () => 288 }),
      }),
    },
    {
      key: 'u13-sparse-history',
      contract: usageContract(),
      label: 'Daily detail narrower than the range',
      description:
        'Daily records start well after the monthly history does. Select 30 days to see the truncation note ("daily detail covers this billing cycle only"); set a custom range entirely before the daily window to see the unavailable note and the Monthly hand-off.',
      group: 'DATA',
      vm: buildUsageVm({
        contracted: 500,
        monthlyHwm: 421,
        cycleStart: '2026-08-24',
        cycleEnd: '2026-09-23',
        daily: buildUsageDaily({
          start: '2026-08-24',
          days: 5,
          contracted: 500,
          billable: (i) => 400 + i * 5,
        }),
        monthly: buildUsageMonthly({ start: '2025-10', months: 12, contracted: 500, hwm: (i) => 280 + i * 13 }),
      }),
    },

    // ─── Scale & shape ───────────────────────────────────────────────
    {
      key: 'u14-azure-only',
      contract: usageContract({ productName: 'Arpio Azure Enterprise', licensedBillableResourceCount: 300 }),
      label: 'Azure-only account',
      description:
        'No AWS applications. Checks the "0 AWS · N Azure" split, the AWS cloud filter emptying every panel, and ARM provider paths rendering as resource-type names in the expanded rows.',
      group: 'SCALE',
      vm: buildUsageVm({
        contracted: 300,
        monthlyHwm: 264,
        cycleStart: START,
        cycleEnd: END,
        appCount: 5,
        appsAws: 0,
        appsAzure: 5,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 300,
          billable: (i) => 220 + i * 2,
          azureShare: 1,
        }),
        monthly: buildUsageMonthly({
          start: '2025-09',
          months: 12,
          contracted: 300,
          hwm: (i) => 160 + i * 9,
          azureShare: 1,
        }),
      }),
    },
    {
      key: 'u15-large-scale',
      contract: usageContract({ licensedBillableResourceCount: 18000, enforceLimits: true }),
      label: 'Large account',
      description:
        'Five-digit counts across a full 62-day daily window and 12 monthly cycles, with ten applications. Stresses KPI number widths, x-axis tick thinning (one label per ceil(n/12) points), and table density.',
      group: 'SCALE',
      vm: buildUsageVm({
        contracted: 18000,
        monthlyHwm: 21450,
        cycleStart: '2026-07-01',
        cycleEnd: '2026-08-31',
        appCount: 10,
        daily: buildUsageDaily({
          start: '2026-07-01',
          days: 62,
          contracted: 18000,
          billable: (i) => 16800 + Math.round(Math.sin(i / 5) * 2400) + i * 40,
        }),
        monthly: buildUsageMonthly({
          start: '2025-09',
          months: 12,
          contracted: 18000,
          hwm: (i) => 12500 + i * 780,
        }),
      }),
    },
    {
      key: 'u16-enterprise-cap',
      contract: usageContract({ licensedBillableResourceCount: 1000000 }),
      label: 'Enterprise cap',
      description:
        'A seven-digit contracted cap against four-digit usage — the real shape of a large-contract account. Stresses grouped KPI figures, and it is the flagship case for the cap leaving the chart: the y axis retightens around the series, the dashed line is replaced by the "(off scale)" note in the corner, and the legend drops its dashed swatch.',
      group: 'SCALE',
      vm: buildUsageVm({
        contracted: 1000000,
        monthlyHwm: 1025,
        cycleStart: '2026-08-01',
        cycleEnd: '2026-08-31',
        appCount: 10,
        daily: buildUsageDaily({
          start: '2026-08-01',
          days: ENTERPRISE_CAP_DAILY.length,
          contracted: 1000000,
          billable: (i) => ENTERPRISE_CAP_DAILY[i],
        }),
        monthly: buildUsageMonthly({
          start: '2025-09',
          months: 12,
          contracted: 1000000,
          hwm: (i) => 720 + i * 28,
        }),
      }),
    },
    {
      key: 'u16b-large-cap-in-scale',
      contract: usageContract({ licensedBillableResourceCount: 2000000 }),
      label: 'Large cap, heavily used',
      description:
        'A two-million cap used past half, so the cap stays in the domain — the only shape that drives the axis into millions. Ticks compact to 500k / 1M / 1.5M / 2M while the dashed contracted line stays drawn.',
      group: 'SCALE',
      vm: buildUsageVm({
        contracted: 2000000,
        monthlyHwm: 1460000,
        cycleStart: START,
        cycleEnd: END,
        appCount: 10,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 2000000,
          billable: (i) => 1200000 + Math.round(Math.sin(i / 2.4) * 90000) + i * 12000,
        }),
        monthly: standardMonthly(2000000, 1460000),
      }),
    },
    {
      key: 'u17-single-app',
      contract: usageContract({
        productName: 'Arpio Standard',
        licensedBillableResourceCount: 200,
      }),
      label: 'Single application',
      description:
        'One application carries the whole account. The breakdown bar is full width (it is its own denominator), top-3 share reads 100%, and average-per-app equals the total.',
      group: 'SCALE',
      vm: buildUsageVm({
        contracted: 200,
        monthlyHwm: 174,
        cycleStart: START,
        cycleEnd: END,
        appCount: 1,
        appsAws: 1,
        appsAzure: 0,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 200,
          billable: (i) => 150 + Math.round(Math.sin(i / 2) * 18) + i,
          azureShare: 0,
        }),
        monthly: standardMonthly(200, 174),
      }),
    },

    {
      key: 'u17b-beyond-top-ten',
      contract: usageContract({ licensedBillableResourceCount: 900 }),
      label: 'More applications than the table shows',
      description:
        'A 19-application account where 14 billed during the cycle. The only scenario that drives both halves of the exclusion note: the top-10 cap withholds 4 of the applications that billed, and the remaining 5 billed nothing at all, so the footer reads as one tally. Switch the cloud filter to check the counts narrow per cloud, and search to check the note goes silent.',
      group: 'SCALE',
      vm: buildUsageVm({
        contracted: 800,
        monthlyHwm: 742,
        cycleStart: START,
        cycleEnd: END,
        appCount: 14,
        appsAws: 13,
        appsAzure: 6,
        daily: buildUsageDaily({
          start: START,
          days: DAYS,
          contracted: 800,
          billable: (i) => 640 + Math.round(Math.sin(i / 2.2) * 60) + i * 4,
        }),
        monthly: standardMonthly(800, 742),
      }),
    },

    // ─── Lifecycle ───────────────────────────────────────────────────
    {
      key: 'u18-loading',
      contract: usageContract(),
      label: 'Loading',
      description:
        'loadDashboard never emits, holding the page in its loading state so the inline spinner and label can be reviewed.',
      group: 'LIFECYCLE',
      vm: null,
      lifecycle: 'loading',
    },
    {
      key: 'u19-error',
      contract: usageContract(),
      label: 'Load failure',
      description:
        'loadDashboard errors. The page renders its error notice; on the real page the shared toast handler also fires, which this preview does not reproduce.',
      group: 'LIFECYCLE',
      vm: null,
      lifecycle: 'error',
    },
  ]
}

// ─────────────────────────────────────────────────────────────────────
// Tooltip showcase
// ─────────────────────────────────────────────────────────────────────
// Every tooltip flavor the app can produce, so arrow/bubble alignment can be
// checked against each style class, position, content shape and host shape at
// once. PrimeNG measures the .p-tooltip root to center the bubble and place the
// arrow, so the cap-bearing class and the content's wrapping behavior together
// decide whether the arrow lands correctly.
